import type { ArticleNavItem } from '../types';

type TreeEntry = {
  item: ArticleNavItem;
  parentId: string | null;
  index: number;
  depth: number;
};

export type TreeSyncOperation =
  | { type: 'remove'; id: string }
  | { type: 'insert'; parentId: string | null; index: number; item: ArticleNavItem }
  | { type: 'move'; id: string; parentId: string | null; index: number }
  | { type: 'update'; id: string; item: ArticleNavItem };

function flattenArticles(items: ArticleNavItem[], parentId: string | null = null, depth = 0, result: TreeEntry[] = []): TreeEntry[] {
  items.forEach((item, index) => {
    result.push({ item, parentId, index, depth });
    if (item.items?.length) {
      flattenArticles(item.items, item.id, depth + 1, result);
    }
  });

  return result;
}

function haveSameValue(a: ArticleNavItem, b: ArticleNavItem) {
  return (
    a.label === b.label &&
    a.href === b.href &&
    a.status === b.status &&
    a.hasChildren === b.hasChildren &&
    a.isLoadingChildren === b.isLoadingChildren
  );
}

export function buildArticleTreeSyncOperations(currentArticles: ArticleNavItem[], nextArticles: ArticleNavItem[]): TreeSyncOperation[] {
  const currentEntries = flattenArticles(currentArticles);
  const nextEntries = flattenArticles(nextArticles);

  const currentMap = new Map(currentEntries.map((entry) => [entry.item.id, entry]));
  const nextMap = new Map(nextEntries.map((entry) => [entry.item.id, entry]));
  const operations: TreeSyncOperation[] = [];

  currentEntries
    .filter((entry) => !nextMap.has(entry.item.id))
    .sort((a, b) => b.depth - a.depth)
    .forEach((entry) => {
      operations.push({ type: 'remove', id: entry.item.id });
    });

  nextEntries.forEach((entry) => {
    const currentEntry = currentMap.get(entry.item.id);

    if (!currentEntry) {
      operations.push({
        type: 'insert',
        parentId: entry.parentId,
        index: entry.index,
        item: entry.item,
      });

      return;
    }

    if (currentEntry.parentId !== entry.parentId || currentEntry.index !== entry.index) {
      operations.push({
        type: 'move',
        id: entry.item.id,
        parentId: entry.parentId,
        index: entry.index,
      });
    }

    if (!haveSameValue(currentEntry.item, entry.item)) {
      operations.push({
        type: 'update',
        id: entry.item.id,
        item: entry.item,
      });
    }
  });

  return operations;
}
